import React, { useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from '@mui/material';
import { CheckCircle, CheckCircleOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  getPasswordPolicyRuleResults,
  getPasswordStrength,
  PASSWORD_POLICY_RULES,
} from '../utils/passwordPolicy';

const ShieldIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 22, height: 22, flexShrink: 0 }}
    aria-hidden
  >
    <path
      d="M12 2L4 5.5v6.2c0 5.1 3.5 9.9 8 11.3 4.5-1.4 8-6.2 8-11.3V5.5L12 2z"
      fill="#1e3c72"
    />
    <path d="M12 2v22c4.5-1.4 8-6.2 8-11.3V5.5L12 2z" fill="#2a5298" />
    <path d="M12 2L4 5.5v6.2c0 2.5 1 4.9 2.5 6.8L12 24V2z" fill="#1a3461" />
    <path d="M12 2l8 3.5v6.2c0 2.5-1 4.9-2.5 6.8L12 24V2z" fill="#3b6cb7" />
  </Box>
);

const StrengthShieldIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 18, height: 18, flexShrink: 0 }}
    aria-hidden
  >
    <path
      d="M12 2L4 5.5v6.2c0 5.1 3.5 9.9 8 11.3 4.5-1.4 8-6.2 8-11.3V5.5L12 2z"
      fill="none"
      stroke="#6581bd"
      strokeWidth="1.5"
    />
    <path
      d="M9.2 12.2l1.8 1.8 3.8-3.8"
      fill="none"
      stroke="#6581bd"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

export type PasswordStrengthIndicatorProps = {
  password?: string;
  sx?: SxProps<Theme>;
};

export const PasswordStrengthIndicator = ({ password = '', sx }: PasswordStrengthIndicatorProps) => {
  const strength = getPasswordStrength(password);

  return (
    <Box sx={{ mt: 1.5, mb: 1.5, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <StrengthShieldIcon />
        <Typography
          sx={{
            fontSize: '14px',
            color: '#6581bd',
            lineHeight: 1.4,
          }}
        >
          Şifre Gücü:{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#6581bd' }}>
            {strength.label}
          </Box>
        </Typography>
      </Box>
      <Box
        sx={{
          height: 7,
          borderRadius: '999px',
          bgcolor: '#e8e7f0',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${strength.percent}%`,
            bgcolor: strength.color,
            borderRadius: '999px',
            transition: 'width 0.3s ease, background-color 0.3s ease',
          }}
        />
      </Box>
    </Box>
  );
};

export type PasswordPolicyRulesProps = {
  password?: string;
  compact?: boolean;
  sx?: SxProps<Theme>;
};

export const PasswordPolicyRules = ({ password = '', compact = false, sx }: PasswordPolicyRulesProps) => {
  const ruleResults = getPasswordPolicyRuleResults(password);

  return (
    <Box
      sx={{
        p: compact ? 2 : 2.5,
        borderRadius: '12px',
        bgcolor: '#f4f3fa',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 1 : 1,
          mb: compact ? 1.5 : 1.75,
        }}
      >
        <ShieldIcon />
        <Typography
          sx={{
            fontSize: compact ? '15px' : '16px',
            fontWeight: 400,
            color: '#001250',
            lineHeight: 1.4,
          }}
        >
          Şifre Güvenliği
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: compact ? 1.25 : 1.5,
        }}
      >
        {PASSWORD_POLICY_RULES.map((rule) => {
          const passed = ruleResults[rule.id];
          const iconColor = passed ? '#2e7d5a' : '#001250';
          const textColor = '#001250';
          const iconSize = compact ? 18 : 20;

          return (
            <Box
              component="li"
              key={rule.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: compact ? 1 : 1.25,
              }}
            >
              {passed ? (
                <CheckCircle sx={{ fontSize: iconSize, color: iconColor, flexShrink: 0, mt: '2px' }} />
              ) : (
                <CheckCircleOutline
                  sx={{ fontSize: iconSize, color: iconColor, flexShrink: 0, mt: '2px' }}
                />
              )}
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '21px',
                  letterSpacing: '-0.4px',
                  verticalAlign: 'middle',
                  color: textColor,
                  transition: 'color 0.2s ease',
                }}
              >
                {rule.displayLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export type PasswordPolicyFieldProps = Omit<TextFieldProps, 'type'> & {
  showPolicyRules?: boolean;
  showStrengthIndicator?: boolean;
  policyValue?: string;
};

const PasswordPolicyField = React.forwardRef<HTMLInputElement, PasswordPolicyFieldProps>(
  ({ showPolicyRules = false, showStrengthIndicator = false, policyValue, InputProps, sx, onChange, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const displayValue = policyValue ?? (props.value as string) ?? '';

    return (
      <Box sx={{ width: '100%', ...sx }}>
        <TextField
          {...props}
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          onChange={(event) => {
            const value = event.target.value.replace(/\s/g, '');
            if (value !== event.target.value) {
              event.target.value = value;
            }
            onChange?.(event);
          }}
          InputProps={{
            ...InputProps,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {showStrengthIndicator && <PasswordStrengthIndicator password={displayValue} />}

        {showPolicyRules && <PasswordPolicyRules password={displayValue} sx={{ mt: 1.5 }} />}
      </Box>
    );
  }
);

PasswordPolicyField.displayName = 'PasswordPolicyField';

export default PasswordPolicyField;
